/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 🔔 useNotificationSync — convert in-memory dashboard signals into
/// persisted Firestore notifications.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Two writers today:
///
///   syncCostAnomalies(anomalies)
///     Called from /dashboards/overview after `fetchAnomalies` lands.
///     Creates one notification per anomaly with a stable doc ID
///     (the anomaly's own `anom_<warehouse>_<YYYYMMDD>` key) so
///     re-syncs are idempotent and the user's read/dismiss state
///     survives refreshes.
///
///   syncLongRunningQueries(queries)
///     Called from /dashboards/overview after `fetchHealth` lands.
///     One notification per still-running query > 5 minutes; doc ID is
///     `query_<queryId>` so the same query doesn't double-notify on
///     subsequent health probes.
///
/// Both writers use `setDoc(..., { merge: true })` and intentionally
/// DO NOT include `readBy` / `dismissedBy` in the payload — those
/// fields are owned by user actions and would be clobbered by a
/// re-sync otherwise. They also seed `readBy: []` / `dismissedBy: []`
/// only on FIRST creation via a missing-doc check, so existing rows
/// keep whatever read state members have set.
///
/// Account messages are intentionally not synthesized client-side —
/// admins write directly to the same collection (or a Cloud Function
/// does on their behalf), and the notifications listener picks them
/// up like any other row.

import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
  type Firestore,
} from 'firebase/firestore'
import type {
  AppNotification,
  NotificationLink,
  NotificationSource,
} from '~/stores/notificationsStore'
import type { CostAnomaly } from '~/scripts/anomalyRecommendations'

interface LongRunningQuery {
  query_id: string
  user_name: string
  query_text: string
  duration_sec: number
}

export const useNotificationSync = () => {
  const { accountId } = storeToRefs(useGlobalsStore())

  /** Common write path. Only sets the fields the writer owns; uses
   *  merge: true so any read-state fields stay intact. The first time
   *  we write a given doc id, we also seed empty arrays for
   *  readBy/dismissedBy so the listener doesn't trip on undefined. */
  async function upsert(
    docId: string,
    payload: Omit<AppNotification, 'id' | 'readBy' | 'dismissedBy'>,
  ) {
    if (!accountId.value) return
    const { $firestore } = useNuxtApp()
    const db = $firestore as Firestore
    const ref = doc(db, 'accounts', accountId.value, 'notifications', docId)
    try {
      // Check existence first to decide whether to seed read-state
      // arrays. Doing it as a single getDoc + setDoc round-trip rather
      // than a transaction since we're idempotent + this is fire-and-
      // forget UX (the listener will reconcile either way).
      const snap = await getDoc(ref)
      const seed = snap.exists()
        ? {}
        : { readBy: [], dismissedBy: [] }
      await setDoc(ref, { ...payload, ...seed }, { merge: true })
    } catch (err) {
      console.warn('[useNotificationSync] upsert failed:', docId, err)
    }
  }

  /**
   * Convert a Snowflake-anomalies fetch into notifications. Idempotent —
   * call after every refresh; existing rows are merged with current
   * data without disturbing read state.
   */
  async function syncCostAnomalies(anomalies: CostAnomaly[]) {
    if (!anomalies?.length) return
    for (const a of anomalies) {
      // Anomaly id is already stable (anom_<warehouse>_YYYYMMDD), so we
      // reuse it as the doc id. This makes mark-read in one tab visible
      // to all other tabs on the same anomaly without extra plumbing.
      const day = (a.timestamp || '').slice(0, 10)
      const source: NotificationSource = {
        anomalyId: a.id,
        warehouse: a.warehouse,
        day,
        cost: a.cost,
        expectedCost: a.expectedCost,
      }
      const link: NotificationLink = {
        path: '/dashboards/overview',
        query: { anomaly: a.id },
      }
      // Severity passes through 1:1 — the SQL already classifies into
      // info / warning / critical based on σ above baseline.
      await upsert(a.id, {
        type: 'cost_anomaly',
        severity: a.severity,
        title: `Cost spike on ${a.warehouse}`,
        body: a.description,
        createdAt: timestampFromIso(a.timestamp),
        source,
        link,
      })
    }
  }

  /**
   * Surface currently-running queries that have crossed an attention
   * threshold (5 min) as notifications. We treat any query above the
   * threshold as a `warning` — it's not necessarily wrong, but a human
   * should look. Long enough (30 min) to be expensive escalates to
   * `critical` so the toast container picks it up.
   */
  async function syncLongRunningQueries(queries: LongRunningQuery[]) {
    if (!queries?.length) return
    const now = Timestamp.now()
    for (const q of queries) {
      if (q.duration_sec < 5 * 60) continue
      const severity = q.duration_sec >= 30 * 60 ? 'critical' : 'warning'
      const docId = `query_${q.query_id}`
      const minutes = Math.round(q.duration_sec / 60)
      const source: NotificationSource = {
        queryId: q.query_id,
        userName: q.user_name,
        durationSec: q.duration_sec,
      }
      const link: NotificationLink = {
        path: '/monitoring/query-history',
        query: { query: q.query_id },
      }
      await upsert(docId, {
        type: 'long_running_query',
        severity,
        title: `Query running ${minutes} min on Snowflake`,
        body: `${q.user_name} has a query that's been running ${minutes} minutes.${
          q.query_text ? ` Preview: ${q.query_text.slice(0, 160)}…` : ''
        } Consider opening it in Snowsight to check the profile and decide whether to cancel.`,
        // We don't have a "started at" — use now() for createdAt; the
        // listener orders by createdAt desc and that ordering is fine
        // for in-flight queries (they're all happening "now").
        createdAt: now,
        source,
        link,
      })
    }
  }

  return {
    syncCostAnomalies,
    syncLongRunningQueries,
  }
}

/** Convert an ISO 8601 string to a Firestore Timestamp. Returns null
 *  if the input is empty / unparseable so the doc lands without a
 *  createdAt rather than throwing — the listener orders nulls last,
 *  which is the right fallback. */
function timestampFromIso(iso: string | null | undefined): Timestamp | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return null
  return Timestamp.fromMillis(ms)
}
